import { useState, useRef } from "react";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { SpeechRecognition } from "@capacitor-community/speech-recognition";
import { BASE_API_URL } from "../../utils/consts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ShiftBase {
  userId: string;
  clientId: string;
  employeeFullName: string;
  shiftType: string;
  locationId: string | null;
  locationName: string | null;
}

export interface SingleShift extends ShiftBase {
  isMultipleShift: false;
  date: string;       // "YYYY-MM-DD"
  startTime: string;  // "HH:mm"
  endTime: string;    // "HH:mm"
  durationInHours: number;
}

export interface MultiShift extends ShiftBase {
  isMultipleShift: true;
  startDate: string;            // "YYYY-MM-DD"
  startTime: string;            // "HH:mm"
  endTime: string;              // "HH:mm"
  timeFrame: "week" | "month";
  durationInHours: number;
  customExcludeDates: string[]; // dates within the timeFrame to skip
}

export interface LeaveShift extends ShiftBase {
  isMultipleShift: true;
  shiftType: "LEAVES";
  startDate: string;
  leaveDates: string[];   // ["YYYY-MM-DD", ...]
  leaveTypeName: string;
  leavePayType: string;
  leavePayPercentage: number;
}

export type ResolvedShift = SingleShift | MultiShift | LeaveShift;

interface DisambiguationOption {
  _id: string;
  firstName: string;
  lastName: string;
}

export interface DeletableShift {
  _id: string;
  date: string;
  startTime: string;
  endTime: string;
  shiftType: string;
  unitId?: string | null;
}

export type VoiceShiftState =
  | { status: "idle" }
  | { status: "listening"; interimTranscript: string }
  | { status: "parsing"; transcript: string }
  | { status: "preview"; shift: ResolvedShift; ambiguities: string[] }
  | {
      status: "disambiguation";
      options: DisambiguationOption[];
      partial: any;
    }
  | { status: "saving" }
  | { status: "delete-preview"; shifts: DeletableShift[]; employeeFullName: string; userId: string }
  | { status: "deleting" }
  | { status: "error"; message: string };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useVoiceShift(clientId: string) {
  const [state, setState] = useState<VoiceShiftState>({ status: "idle" });
  const [lang, setLang] = useState<"bcms" | "sl" | "en">("bcms");
  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef<string>("");

  const startListening = () => {
    if (Capacitor.isNativePlatform()) {
      startListeningNative();
    } else {
      startListeningWeb();
    }
  };

  const startListeningNative = async () => {
    finalTranscriptRef.current = "";
    setState({ status: "listening", interimTranscript: "" });

    try {
      const { available } = await SpeechRecognition.available();
      if (!available) {
        setState({ status: "error", message: "Prepoznavanje govora nije dostupno na ovom uređaju." });
        return;
      }

      const perm = await SpeechRecognition.requestPermissions();
      if (perm.speechRecognition !== "granted") {
        setState({ status: "error", message: "Pristup mikrofonu odbijen. Dozvoli pristup u Podešavanja → Privatnost → Mikrofon." });
        return;
      }

      // iOS locale identifiers koriste underscore (sr_RS / hr_HR), provjeri šta uređaj podržava
      const language =
        lang === "en" ? "en_US" :
        lang === "sl" ? "sl_SI" :
        await pickBestSerbianLocale();
      if (!language) {
        setState({ status: "error", message: "Glasovni unos nije dostupan. Pokušaj dodati Hrvatski (hr) u Podešavanja → Opšte → Jezik i region." });
        return;
      }

      await SpeechRecognition.addListener("partialResults", (data: { matches: string[] }) => {
        const text = data.matches?.[0] ?? "";
        finalTranscriptRef.current = text;
        setState({ status: "listening", interimTranscript: text });
      });

      // listeningState: fired when recognition ends naturally (silence/timeout)
      await SpeechRecognition.addListener("listeningState", (data: { status: string }) => {
        if (data.status === "stopped" && recognitionRef.current === "native") {
          recognitionRef.current = null;
          SpeechRecognition.removeAllListeners();
          const transcript = finalTranscriptRef.current.trim();
          if (transcript) parseTranscript(transcript);
          else setState({ status: "idle" });
        }
      });

      recognitionRef.current = "native";

      // start() se razriješi odmah kada je partialResults:true —
      // rezultati dolaze asinhorno kroz partialResults i listeningState evente
      await SpeechRecognition.start({
        language,
        maxResults: 1,
        partialResults: true,
        popup: false,
      });
    } catch (err: any) {
      await SpeechRecognition.removeAllListeners();
      recognitionRef.current = null;
      setState({ status: "error", message: "Nije moguće pokrenuti prepoznavanje govora. Pokušaj ponovo." });
    }
  };

  const startListeningWeb = () => {
    const WebSpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!WebSpeechRecognition) {
      setState({
        status: "error",
        message:
          "Voice input is not supported in this browser. Please use Chrome or Edge.",
      });
      return;
    }

    finalTranscriptRef.current = "";

    const recognition = new WebSpeechRecognition();
    recognition.lang = lang === "en" ? "en-US" : lang === "sl" ? "sl-SI" : "hr-HR";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognitionRef.current = recognition;
    setState({ status: "listening", interimTranscript: "" });

    recognition.onresult = (event: any) => {
      let interim = "";
      let newFinal = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinal += text + " ";
        } else {
          interim += text;
        }
      }
      if (newFinal) finalTranscriptRef.current += newFinal;
      setState({
        status: "listening",
        interimTranscript: (finalTranscriptRef.current + interim).trim(),
      });
    };

    recognition.onerror = (event: any) => {
      recognitionRef.current = null;
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setState({ status: "error", message: "Pristup mikrofonu odbijen. Dozvoli pristup mikrofonu u podešavanjima browsera." });
      } else if (event.error === "audio-capture") {
        setState({ status: "error", message: "Mikrofon nije dostupan. Provjeri da li je priključen i dozvoljen." });
      } else if (event.error !== "aborted") {
        setState({
          status: "error",
          message: "Nije moguće snimiti glas. Pokušaj ponovo.",
        });
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      const transcript = finalTranscriptRef.current.trim();
      if (transcript) {
        parseTranscript(transcript);
      } else {
        setState({ status: "idle" });
      }
    };

    recognition.start();
  };

  const stopListening = async () => {
    if (recognitionRef.current === "native") {
      recognitionRef.current = null;
      await SpeechRecognition.stop();
      await SpeechRecognition.removeAllListeners();
      const transcript = finalTranscriptRef.current.trim();
      if (transcript) parseTranscript(transcript);
      else setState({ status: "idle" });
    } else {
      recognitionRef.current?.stop();
    }
  };

  const parseTranscript = async (transcript: string) => {
    setState({ status: "parsing", transcript });
    try {
      const { data } = await axios.post(`${BASE_API_URL}/voice-shift/parse`, {
        transcript,
        clientId,
        currentDate: new Date().toISOString(),
      });

      if (data.deletePreview) {
        setState({
          status: "delete-preview",
          shifts: data.deletePreview,
          employeeFullName: data.employeeFullName,
          userId: String(data.userId),
        });
      } else if (data.disambiguation) {
        setState({
          status: "disambiguation",
          options: data.disambiguation,
          partial: data.partial,
        });
      } else {
        setState({
          status: "preview",
          shift: data.shift,
          ambiguities: data.ambiguities || [],
        });
      }
    } catch (err: any) {
      setState({
        status: "error",
        message:
          err.response?.data?.message ||
          "Failed to parse command. Try: 'Add shift for Marko tomorrow at 10'",
      });
    }
  };

  const resolveDisambiguation = async (employee: DisambiguationOption, partial: any) => {
    // Delete intent: fetch shifts from backend then show delete preview
    if (partial.intent === "delete") {
      setState({ status: "parsing", transcript: `Finding shifts for ${employee.firstName}…` });
      try {
        const { data } = await axios.post(`${BASE_API_URL}/voice-shift/delete-preview`, {
          userId: employee._id,
          clientId,
          startDate: partial.date || null,
          endDate: partial.endDate || partial.date || null,
          shiftType: partial.shiftType || null,
          specificDates: partial.specificDates || null,
        });
        setState({
          status: "delete-preview",
          shifts: data.deletePreview,
          employeeFullName: `${employee.firstName} ${employee.lastName}`,
          userId: String(employee._id),
        });
      } catch (err: any) {
        setState({
          status: "error",
          message: err.response?.data?.message || "Failed to find shifts to delete.",
        });
      }
      return;
    }

    // Create intent: Re-build the shift with the chosen employee, rest stays the same
    const ambiguities: string[] = [];
    const shiftType = partial.shiftType || "HOURS";

    if (shiftType === "LEAVES") {
      setState({
        status: "preview",
        shift: {
          userId: employee._id,
          clientId,
          employeeFullName: `${employee.firstName} ${employee.lastName}`,
          shiftType: "LEAVES",
          locationId: null,
          locationName: partial.locationName || null,
          isMultipleShift: true,
          startDate: partial.date,
          leaveDates: partial.endDate
            ? generateDateRange(partial.date, partial.endDate)
            : [partial.date],
          leaveTypeName: partial.leaveTypeName || "Neodređeno",
          leavePayType: partial.leavePayType || "DAILY_WAGE",
          leavePayPercentage: partial.leavePayPercentage ?? 100,
        } as LeaveShift,
        ambiguities,
      });
      return;
    }

    const { startTime, endTime } = applyDefaults(
      shiftType,
      partial.startTime,
      partial.endTime
    );

    if (partial.isMultipleShift) {
      setState({
        status: "preview",
        shift: {
          userId: employee._id,
          clientId,
          employeeFullName: `${employee.firstName} ${employee.lastName}`,
          shiftType,
          locationId: null,
          locationName: partial.locationName || null,
          isMultipleShift: true,
          startDate: partial.date,
          startTime,
          endTime,
          timeFrame: partial.timeFrame || "week",
          customExcludeDates: partial.customExcludeDates ?? [],
          durationInHours: calcDuration(startTime, endTime),
        } as MultiShift,
        ambiguities,
      });
      return;
    }

    if (!partial.endTime) {
      ambiguities.push("End time was not specified — defaulted based on shift type");
    }

    setState({
      status: "preview",
      shift: {
        userId: employee._id,
        clientId,
        employeeFullName: `${employee.firstName} ${employee.lastName}`,
        shiftType,
        locationId: null,
        locationName: partial.locationName || null,
        isMultipleShift: false,
        date: partial.date,
        startTime,
        endTime,
        durationInHours: calcDuration(startTime, endTime),
      } as SingleShift,
      ambiguities,
    });
  };

  const confirmShift = async (shift: ResolvedShift): Promise<boolean> => {
    setState({ status: "saving" });
    try {
      if (!shift.isMultipleShift) {
        // ── Single shift ──────────────────────────────────────────────────
        const s = shift as SingleShift;
        const payload: any = {
          userId: s.userId,
          clientId: s.clientId,
          date: s.date,
          startTime: `${s.date}T${s.startTime}:00`,
          endTime: `${s.date}T${s.endTime}:00`,
          shiftType: s.shiftType,
        };
        if (s.locationId) payload.locationId = s.locationId;
        await axios.post(`${BASE_API_URL}/shifts/create`, payload);
      } else if (shift.shiftType === "LEAVES") {
        // ── Leave shift ───────────────────────────────────────────────────
        const l = shift as LeaveShift;
        const payload: any = {
          userId: l.userId,
          clientId: l.clientId,
          startDate: l.startDate,
          shiftStartTime: "00:00",
          shiftEndTime: "23:59",
          shiftType: "LEAVES",
          timeFrame: l.leaveTypeName,
          excludeDays: [],
          customExcludeDates: l.leaveDates,
          leavePayPercentage: l.leavePayPercentage,
          leavePayType: l.leavePayType,
        };
        if (l.locationId) payload.locationId = l.locationId;
        await axios.post(`${BASE_API_URL}/create-multiple-shifts`, payload);
      } else {
        // ── Multi shift (week / month) ────────────────────────────────────
        const m = shift as MultiShift;
        const payload: any = {
          userId: m.userId,
          clientId: m.clientId,
          startDate: m.startDate,
          shiftStartTime: m.startTime,
          shiftEndTime: m.endTime,
          shiftType: m.shiftType,
          timeFrame: m.timeFrame,
          excludeDays: [],
          customExcludeDates: m.customExcludeDates ?? [],
        };
        if (m.locationId) payload.locationId = m.locationId;
        await axios.post(`${BASE_API_URL}/create-multiple-shifts`, payload);
      }

      setState({ status: "idle" });
      return true;
    } catch (err: any) {
      setState({
        status: "error",
        message:
          err.response?.data?.message || "Failed to create shift. Try again.",
      });
      return false;
    }
  };

  const confirmDelete = async (shifts: DeletableShift[]): Promise<boolean> => {
    setState({ status: "deleting" });
    try {
      await Promise.all(
        shifts.map((s) =>
          axios.delete(`${BASE_API_URL}/shifts/delete`, { params: { shiftId: s._id } })
        )
      );
      setState({ status: "idle" });
      return true;
    } catch (err: any) {
      setState({
        status: "error",
        message: err.response?.data?.message || "Failed to delete shifts. Try again.",
      });
      return false;
    }
  };

  const reset = async () => {
    if (recognitionRef.current === "native") {
      await SpeechRecognition.stop().catch(() => {});
      await SpeechRecognition.removeAllListeners().catch(() => {});
    } else if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    recognitionRef.current = null;
    finalTranscriptRef.current = "";
    setState({ status: "idle" });
  };

  return {
    state,
    lang,
    setLang,
    startListening,
    stopListening,
    resolveDisambiguation,
    confirmShift,
    confirmDelete,
    reset,
  };
}

// ---------------------------------------------------------------------------
// Helpers (mirrored from backend for local disambiguation resolution)
// ---------------------------------------------------------------------------
const SHIFT_DEFAULTS: Record<string, { start: string; minutes: number }> = {
  DAILY_WAGE: { start: "08:00", minutes: 510 },
  TEMPORARY_HELP: { start: "08:00", minutes: 240 },
  "Morning Shift": { start: "06:00", minutes: 480 },
  "Afternoon Shift": { start: "14:00", minutes: 480 },
  "Night Shift": { start: "22:00", minutes: 480 },
  HOURS: { start: "08:00", minutes: 480 },
};

function applyDefaults(shiftType: string, startTime: string | null, endTime: string | null) {
  const def = SHIFT_DEFAULTS[shiftType] || SHIFT_DEFAULTS.HOURS;
  const resolvedStart = startTime || def.start;
  const resolvedEnd = endTime || addMinutes(resolvedStart, def.minutes);
  return { startTime: resolvedStart, endTime: resolvedEnd };
}

function addMinutes(timeStr: string, minutes: number): string {
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function calcDuration(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let diff = eh * 60 + em - (sh * 60 + sm);
  if (diff < 0) diff += 24 * 60;
  return parseFloat((diff / 60).toFixed(2));
}

function generateDateRange(startStr: string, endStr: string): string[] {
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  const dates: string[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(cur.toISOString().split("T")[0]);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

async function pickBestSerbianLocale(): Promise<string | null> {
  try {
    const { languages } = await SpeechRecognition.getSupportedLanguages();
    const langs = languages as string[];
    // iOS locale identifiers koriste underscore: sr_RS, hr_HR itd.
    // Pokušaj srpski, pa hrvatski (isti govorni jezik — Apple podržava hr_HR)
    const preferred = ["sr_RS", "sr-RS", "sr_Latn_RS", "sr-Latn-RS", "sr", "hr_HR", "hr-HR", "hr", "en_US", "en-US", "en_GB", "en-GB", "en"];
    for (const p of preferred) {
      if (langs.includes(p)) return p;
    }
    const any = langs.find((l) => l.startsWith("sr") || l.startsWith("hr") || l.startsWith("en"));
    if (any) return any;
  } catch {}
  return null;
}
